import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Sparkles, Plus, ChevronRight, Database, Sprout, Loader2 } from "lucide-react";
import { parseInco } from "../lib/incoParser";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Suggère des ingrédients de recette à partir du texte INCO (étiquette).
 * - Auto-matching avec la BD ingrédients : nom normalisé (insensible casse/accents).
 *   → Si match BD, auto-remplissage prix/unité/fournisseur.
 * - Pour les non-matches, option « Créer dans la BD » pour enrichir le référentiel.
 *
 * Props:
 * - inco: string — texte INCO du produit
 * - existing: array<{name}> — ingrédients déjà dans la recette
 * - onAdd: (items) => void — appelé avec les lignes recette à ajouter
 * - ingredientsDb: array — référentiel BD ingrédients
 * - onCreateIngredient?: (newIng) => void — callback quand un ingrédient est créé en BD
 */

// Normalise un nom : minuscules, sans accents, espaces simples, sans ponctuation finale
function normalizeName(s) {
  if (!s) return "";
  return s
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/[*•·]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function SuggestIngredientsFromInco({ inco, existing = [], onAdd, ingredientsDb = [], onCreateIngredient }) {
  const [open, setOpen] = useState(false);
  const [includeSubItems, setIncludeSubItems] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // Pour les lignes non-matchées : map nom → booléen "créer dans la BD"
  const [toCreate, setToCreate] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  const parsed = useMemo(() => parseInco(inco || ""), [inco]);

  const existingNames = useMemo(
    () => new Set((existing || []).map((it) => normalizeName(it.name)).filter(Boolean)),
    [existing]
  );

  // Index BD par nom normalisé
  const dbByNorm = useMemo(() => {
    const m = new Map();
    for (const it of ingredientsDb || []) {
      const k = normalizeName(it.name);
      if (k && !m.has(k)) m.set(k, it);
    }
    return m;
  }, [ingredientsDb]);

  // Liste finale : ingrédients principaux + (option) sous-ingrédients. Doublons éliminés.
  const suggestions = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (name, kind, parent) => {
      const key = normalizeName(name);
      if (!key || seen.has(key)) return;
      seen.add(key);
      const alreadyInRecipe = existingNames.has(key);
      const dbMatch = dbByNorm.get(key) || null;
      out.push({ name, kind, parent, alreadyInRecipe, dbMatch, key });
    };
    for (const it of parsed) {
      push(it.name, "main", null);
      if (includeSubItems) {
        for (const sub of it.subItems || []) push(sub, "sub", it.name);
      }
    }
    return out;
  }, [parsed, includeSubItems, existingNames, dbByNorm]);

  // À l'ouverture : pré-cocher les nouveaux. Pour les non-BD, pré-cocher l'option "créer en BD".
  useEffect(() => {
    if (!open) return;
    const sel = new Set();
    const cre = new Set();
    suggestions.forEach((s) => {
      if (!s.alreadyInRecipe) sel.add(s.key);
      if (!s.alreadyInRecipe && !s.dbMatch) cre.add(s.key);
    });
    setSelected(sel);
    setToCreate(cre);
  }, [open, suggestions]);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const toggleCreate = (key) => {
    const next = new Set(toCreate);
    if (next.has(key)) next.delete(key); else next.add(key);
    setToCreate(next);
  };

  const toggleAllSuggestible = () => {
    const suggestible = suggestions.filter((s) => !s.alreadyInRecipe).map((s) => s.key);
    const allOn = suggestible.length > 0 && suggestible.every((k) => selected.has(k));
    const next = new Set(selected);
    if (allOn) {
      suggestible.forEach((k) => next.delete(k));
    } else {
      suggestible.forEach((k) => next.add(k));
    }
    setSelected(next);
  };

  // Construit une ligne recette à partir d'un ingrédient BD
  const buildLineFromDb = (s, dbIng) => ({
    name: dbIng.name,
    quantity: "",
    unit: dbIng.unit || "g",
    note: s.kind === "sub" && s.parent ? `Sous-ingrédient de ${s.parent}` : "",
    unit_price_chf: dbIng.default_price_chf ?? "",
    price_unit: dbIng.default_price_unit || "kg",
    supplier_id: dbIng.default_supplier_id || null,
  });

  const buildLineWithoutDb = (s) => ({
    name: s.name,
    quantity: "",
    unit: "g",
    note: s.kind === "sub" && s.parent ? `Sous-ingrédient de ${s.parent}` : "",
    unit_price_chf: "",
    price_unit: "kg",
    supplier_id: null,
  });

  const apply = async () => {
    const chosen = suggestions.filter((s) => selected.has(s.key) && !s.alreadyInRecipe);
    if (chosen.length === 0) {
      toast.error("Aucun ingrédient à ajouter (déjà présents ?)");
      return;
    }
    setSubmitting(true);
    const lines = [];
    let createdCount = 0;
    let dbMatchCount = 0;
    try {
      for (const s of chosen) {
        if (s.dbMatch) {
          // Match BD : prix/unité/fournisseur auto-remplis
          lines.push(buildLineFromDb(s, s.dbMatch));
          dbMatchCount += 1;
        } else if (toCreate.has(s.key)) {
          // Création BD demandée
          try {
            const { data } = await api.post("/ingredients", { name: s.name });
            if (onCreateIngredient) onCreateIngredient(data);
            lines.push(buildLineFromDb(s, data));
            createdCount += 1;
          } catch (e) {
            // Si l'API refuse (ex: doublon détecté côté serveur), on ajoute la ligne nue
            lines.push(buildLineWithoutDb(s));
          }
        } else {
          // Ni match, ni création → ligne nue (saisie manuelle nécessaire)
          lines.push(buildLineWithoutDb(s));
        }
      }
      onAdd(lines);
      const parts = [`${lines.length} ingrédient${lines.length > 1 ? "s" : ""} ajouté${lines.length > 1 ? "s" : ""}`];
      if (dbMatchCount > 0) parts.push(`${dbMatchCount} pré-rempli${dbMatchCount > 1 ? "s" : ""} depuis la BD`);
      if (createdCount > 0) parts.push(`${createdCount} créé${createdCount > 1 ? "s" : ""} dans la BD`);
      toast.success(parts.join(" · "));
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const newCount = suggestions.filter((s) => !s.alreadyInRecipe).length;
  const matchCount = suggestions.filter((s) => !s.alreadyInRecipe && s.dbMatch).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="no-print border-[#1F4E3D]/30 text-[#1F4E3D] hover:bg-[#E8EEE9]"
          disabled={!inco || !inco.trim()}
          data-testid="suggest-from-inco-btn"
          title={!inco?.trim() ? "Aucune déclaration d'ingrédients sur le produit" : "Pré-remplir depuis la déclaration étiquette"}
        >
          <Sparkles className="w-4 h-4 mr-1" /> Suggérer depuis l'étiquette
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E5E5E0]">
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#1F4E3D]" />
            Suggérer des ingrédients depuis l'étiquette
          </DialogTitle>
          <DialogDescription>
            Extrait automatiquement les ingrédients. Ceux déjà présents dans votre BD sont
            <strong className="text-[#1F4E3D]"> pré-remplis avec prix &amp; fournisseur</strong>.
            Les nouveaux peuvent être <strong>créés dans la BD</strong> en un clic.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {/* Texte INCO source */}
          <div className="text-xs text-[#575A57] bg-[#F9F9F6] border border-[#E5E5E0] rounded p-2 italic">
            <strong className="not-italic">Source :</strong> {inco}
          </div>

          {/* Options */}
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="include-sub"
              checked={includeSubItems}
              onCheckedChange={setIncludeSubItems}
              data-testid="include-sub-items"
            />
            <Label htmlFor="include-sub" className="cursor-pointer">
              Inclure les sous-ingrédients <em className="text-[#A3A6A3]">(entre parenthèses — utile si vous fabriquez la pâte vous-même)</em>
            </Label>
          </div>

          {/* Compteur + select all */}
          {suggestions.length > 0 && (
            <div className="flex items-center justify-between text-xs text-[#575A57] pt-1">
              <div>
                <strong className="text-[#1A1C1A]">{newCount}</strong> nouvel{newCount > 1 ? "s" : ""} ingrédient{newCount > 1 ? "s" : ""}
                {matchCount > 0 && (
                  <span> · <strong className="text-[#1F4E3D]">{matchCount}</strong> reconnu{matchCount > 1 ? "s" : ""} dans la BD</span>
                )}
                {suggestions.length - newCount > 0 && (
                  <span> · {suggestions.length - newCount} déjà dans la recette</span>
                )}
              </div>
              {newCount > 0 && (
                <button
                  type="button"
                  onClick={toggleAllSuggestible}
                  className="text-[#1F4E3D] hover:underline"
                  data-testid="suggest-toggle-all"
                >
                  Tout cocher/décocher
                </button>
              )}
            </div>
          )}

          {/* Liste */}
          {suggestions.length === 0 ? (
            <div className="text-sm text-[#A3A6A3] italic border border-dashed border-[#E5E5E0] rounded p-4 text-center">
              Impossible d'extraire des ingrédients de cette déclaration.
            </div>
          ) : (
            <div className="border border-[#E5E5E0] rounded-lg divide-y divide-[#F0F0EB]">
              {suggestions.map((s, idx) => {
                const isSelected = selected.has(s.key);
                const isSub = s.kind === "sub";
                const willCreate = toCreate.has(s.key);
                return (
                  <div
                    key={`${s.key}-${idx}`}
                    className={`px-3 py-2 ${s.alreadyInRecipe ? "bg-[#F4F4F0] opacity-60" : "hover:bg-[#FAFAF7]"}`}
                    data-testid={`suggest-row-${idx}`}
                  >
                    <div
                      className={`flex items-center gap-3 ${s.alreadyInRecipe ? "" : "cursor-pointer"}`}
                      onClick={() => !s.alreadyInRecipe && toggle(s.key)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => !s.alreadyInRecipe && toggle(s.key)}
                        disabled={s.alreadyInRecipe}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isSub && <ChevronRight className="w-3 h-3 text-[#A3A6A3]" />}
                          <span className={`text-sm ${s.alreadyInRecipe ? "line-through text-[#A3A6A3]" : "text-[#1A1C1A]"}`}>
                            {s.dbMatch ? s.dbMatch.name : s.name}
                          </span>
                          {isSub && s.parent && (
                            <span className="text-[10px] text-[#A3A6A3] italic">({s.parent})</span>
                          )}
                        </div>
                        {/* Détails BD ou hint */}
                        {!s.alreadyInRecipe && s.dbMatch && (
                          <div className="text-[11px] text-[#1F4E3D] font-mono mt-0.5 flex items-center gap-2 pl-0">
                            <Database className="w-3 h-3" />
                            <span>BD : {s.dbMatch.category}</span>
                            {s.dbMatch.default_price_chf != null && (
                              <span>· {Number(s.dbMatch.default_price_chf).toFixed(2)} CHF/{s.dbMatch.default_price_unit || "kg"}</span>
                            )}
                            {s.dbMatch.default_supplier_id && <span>· avec fournisseur</span>}
                          </div>
                        )}
                      </div>
                      {s.alreadyInRecipe ? (
                        <span className="text-[10px] text-[#1F4E3D] uppercase tracking-wider font-medium">
                          ✓ Déjà présent
                        </span>
                      ) : s.dbMatch ? (
                        <span className="text-[10px] text-[#1F4E3D] uppercase tracking-wider font-medium bg-[#E8EEE9] px-1.5 py-0.5 rounded">
                          BD
                        </span>
                      ) : (
                        <span className="text-[10px] text-[#8C6B2F] uppercase tracking-wider font-medium bg-[#FBF4E9] px-1.5 py-0.5 rounded">
                          Nouveau
                        </span>
                      )}
                    </div>
                    {/* Option « créer dans la BD » pour les non-matchs */}
                    {!s.alreadyInRecipe && !s.dbMatch && isSelected && (
                      <div className="flex items-center gap-2 pl-7 mt-1.5">
                        <Checkbox
                          id={`create-${s.key}`}
                          checked={willCreate}
                          onCheckedChange={() => toggleCreate(s.key)}
                          data-testid={`suggest-create-${idx}`}
                        />
                        <Label htmlFor={`create-${s.key}`} className="cursor-pointer text-[11px] text-[#575A57] flex items-center gap-1">
                          <Sprout className="w-3 h-3 text-[#1F4E3D]" />
                          Créer « <strong className="text-[#1A1C1A]">{s.name}</strong> » dans la BD ingrédients (réutilisable)
                        </Label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-xs text-[#575A57]">
            <strong>💡</strong> Les lignes <strong className="text-[#1F4E3D]">BD</strong> arrivent avec prix &amp; fournisseur ;
            les lignes <strong className="text-[#8C6B2F]">Nouveau</strong> non-cochées « Créer en BD » devront être complétées manuellement.
            Quantité = vide dans tous les cas.
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-[#E5E5E0]">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Annuler</Button>
          <Button
            onClick={apply}
            disabled={selected.size === 0 || submitting}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid="suggest-apply-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Ajouter {selected.size > 0 ? `${selected.size} ingrédient${selected.size > 1 ? "s" : ""}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
